import { useEffect } from 'react';

const SITE_URL = 'https://titrate.clinicalcme.ai';
const DEFAULT_TITLE = 'Titrate — CAOP & OPRA exam preparation';
const DEFAULT_DESCRIPTION =
  "Independent practice platform for overseas-qualified pharmacists preparing for the Australian Pharmacy Council's CAOP and OPRA registration exams. Not affiliated with the APC.";

function setMeta(selector: string, attr: 'content' | 'href', value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

/**
 * Keeps the tab title and the sharing/SEO tags (canonical, Open Graph,
 * Twitter Card) in sync with whichever route is actually mounted.
 *
 * `index.html` only ships one static set of these tags, and a client-side
 * route change — this is a single-page app — never re-fetches that file, so
 * without this every page you navigate to keeps showing the homepage's tab
 * title and would preview as the homepage if shared. The server prerenders
 * the same three tags for the handful of routes a crawler might read without
 * running JavaScript at all (see `apps/api/src/routes/legal.ts`); this hook
 * is what keeps every other route — and a real visitor's tab — correct.
 *
 * Call with no arguments to restore the site default, e.g. on unmount.
 */
export function usePageMeta(page?: { title: string; description: string; path: string }) {
  useEffect(() => {
    const title = page ? `${page.title} — Titrate` : DEFAULT_TITLE;
    const description = page?.description ?? DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${page?.path ?? '/'}`;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // Restore the site default on unmount, so navigating away — including to
    // a route that doesn't call this hook — never leaves a stale title/tags
    // from the page just left.
    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="description"]', 'content', DEFAULT_DESCRIPTION);
      setMeta('link[rel="canonical"]', 'href', SITE_URL);
      setMeta('meta[property="og:title"]', 'content', DEFAULT_TITLE);
      setMeta('meta[property="og:description"]', 'content', DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:url"]', 'content', SITE_URL);
      setMeta('meta[name="twitter:title"]', 'content', DEFAULT_TITLE);
      setMeta('meta[name="twitter:description"]', 'content', DEFAULT_DESCRIPTION);
    };
  }, [page?.title, page?.description, page?.path]);
}
